import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { PdfLinkService } from '../../../services/pdf-link.service';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { BackendApiService } from '../../../services/backend-api.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SharedDataService } from '../../shared/shared.data.service';
import { DocumentPickerComponent } from '../document-picker/document-picker.component';
import { CustomerDataService } from '../../customer/customer.data.service';
import { DocumentService } from '../../document/document.service';
import { ToasterService } from '../../../services/core/toaster.service';
import { LazyPreviewService } from '../../../services/lazy-preview.service';
import { environment } from '../../../../environments/environment';
import { ComposeEmailComponent } from '../../email/compose-email/compose-email.component';
import { downloadPdfFromUrl } from '../download-pdf-from-url';
import { fetchPdfAsObjectUrl } from '../pdf-blob-url';

@Component({
    selector: 'app-export-pdf-shared',
    templateUrl: './export-pdf.component.html',
    styleUrls: ['./export-pdf.component.scss'],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(10px)' }),
                animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
            ])
        ])
    ],
    standalone: false
})
export class ExportPdfSharedComponent implements OnInit {
  private readonly pdfLink = inject(PdfLinkService);
  templates: any = [];
  moduleName: any;
  moduleSlug: any;
  tenant_id: any;
  customerId: any;
  
  // Enhanced State Management
  isLoadingTemplates: boolean = false;
  isProcessing: boolean = false;
  /** True from the click on a picker button until that picker closes — stops a second click stacking a second picker. */
  private pickerOpen = false;
  exportSuccess: boolean = false;
  exportError: string = '';
  buttonStates: { [key: string]: 'normal' | 'loading' | 'success' | 'error' } = {
    download: 'normal',
    preview: 'normal',
    save: 'normal',
    email: 'normal'
  };

  /** Drag state for the drop zone's hover styling — same design as Proposal's own dialog. */
  isDraggingFile: boolean = false;
  /**
   * Files attached through the drop zone / system document picker. They live in
   * their own "Additional files" section — never in the Templates select — and
   * are merged into the PDF for Download, Preview and Save Document.
   */
  additionalFiles: any[] = [];
  /** Off by default: the export is the selected template PDF(s) only. On shows the Additional files section. */
  includeAdditionalFiles = false;
  /** Text under the spinner while isProcessing. */
  processingText = 'Saving PDF to Documents…';

  public sectionForm: UntypedFormGroup = new UntypedFormGroup({
    templates: new UntypedFormControl(null, [Validators.required]),
  });

  constructor(
    public backendApiService: BackendApiService,
    public sharedDataService: SharedDataService,
    public dialogRef: MatDialogRef<ExportPdfSharedComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private customerDataService: CustomerDataService,
    private documentService: DocumentService,
    private toasterService: ToasterService,
    private lazyPreview: LazyPreviewService
  ) {}

  ngOnInit(): void {
    this.moduleName = this.data.moduleName || 'Change Order';
    this.moduleSlug = this.data?.moduleSlug
    this.tenant_id = this.data?.tenant_id
    this.customerId = this.data?.customerId
    this.getProposalTemplates();
  }

  /**
   * Override the office/system default with the customer's saved default template
   * for this module, but only when the customer actually has one that exists in
   * the loaded list. Leaves the already-applied system default untouched
   * otherwise. Does not affect PDF generation — only the initial selection.
   */
  private applyCustomerDefaultTemplate(): void {
    this.customerDataService
      .resolveCustomerDefaultTemplate(
        this.customerId,
        this.moduleSlug || this.moduleName,
        this.templates
      )
      .subscribe((template: any) => {
        if (template) {
          this.sectionForm.get('templates')?.setValue([template]);
        }
      });
  }

  /**
   * Additional files are merged by the backend's generic generate-pdf /
   * save-document merge, so they're offered wherever the module has a
   * generate-pdf route and a record to attach to (same condition Send Email
   * already uses). Elsewhere (e.g. accounting reports) the section is hidden.
   */
  get canAttachFiles(): boolean {
    return this.data?.recordId != null && !!this.resolveGeneratePdfUrl();
  }

  /**
   * Drag-and-drop / click-to-browse block — same design as Proposal's own
   * dialog (ExportToPdfComponent), ported here so every module's dialog
   * looks and behaves the same. A dropped/picked file becomes its own chip
   * in the Templates bar, shown there but never sent anywhere: these are
   * local files, not saved templates or stored Documents, so there's no id
   * the backend could do anything with yet (no module has the "upload a
   * dropped file, then merge it" flow Proposal has — that's intentionally
   * not part of this rollout).
   */
  onDropzoneDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFile = true;
  }

  onDropzoneDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFile = false;
  }

  onDropzoneDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFile = false;
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.addUploadedFiles(files);
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.addUploadedFiles(input.files);
    }
    input.value = '';
  }

  /** File types this dialog can merge. Word and images are converted to PDF by the backend. */
  get allowedExtensions(): string[] {
    return ExportPdfSharedComponent.ALLOWED_EXTENSIONS;
  }

  /** PDF, Word (.doc/.docx) and pictures (JPG/PNG/GIF/WebP, one PDF page each). */
  private static readonly ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'gif', 'webp'];

  /** Pictures get an image icon in the list. */
  isImageFile(file: any): boolean {
    return /\.(jpe?g|png|gif|webp)$/i.test(file?.name ?? '');
  }

  /** The hidden file input's `accept` attribute. */
  get fileInputAccept(): string {
    return this.allowedExtensions.map((extension) => '.' + extension).join(',');
  }

  private addUploadedFiles(files: FileList): void {
    // Validated here — for drag-and-drop too, which the input's `accept` doesn't cover — BEFORE
    // anything is uploaded, so an unsupported file never reaches the record's folder.
    const accepted: File[] = [];
    Array.from(files).forEach((file) => {
      const extension = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
      if (this.allowedExtensions.includes(extension)) {
        accepted.push(file);
      } else {
        this.toasterService.showErrorMessage(`"${file.name}" was not added. Only PDF, Word (.doc, .docx) and image (JPG, PNG, GIF, WebP) files can be attached.`);
      }
    });

    accepted.forEach((file) => {
      this.additionalFiles.push({
        id: `upload-${Date.now()}-${file.name}`,
        name: file.name,
        isUploadedFile: true,
        file,
      });
    });
    this.syncTemplatesValidator();
  }

  /** Click a row and move it: drop re-orders the list — the merged PDF follows this order. */
  dropAdditionalFile(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const files = [...this.additionalFiles];
    moveItemInArray(files, event.previousIndex, event.currentIndex);
    this.additionalFiles = files;
  }

  /** Word (.doc/.docx) files get a document icon instead of the PDF one in the list. */
  isWordFile(file: any): boolean {
    return /\.docx?$/i.test(file?.name ?? '');
  }

  removeAdditionalFile(item: any): void {
    this.additionalFiles = this.additionalFiles.filter((f) => f !== item);
    this.syncTemplatesValidator();
  }

  /**
   * Turning the toggle off hides the section and drops any files already added, so nothing hidden
   * is merged into the PDF; the export goes back to just the selected template(s).
   */
  onToggleAdditionalFiles(on: boolean): void {
    this.includeAdditionalFiles = on;
    if (!on) {
      this.additionalFiles = [];
      this.isDraggingFile = false;
      this.syncTemplatesValidator();
    }
  }

  /** A template is only mandatory when there are no additional files to merge on their own. */
  private syncTemplatesValidator(): void {
    const control = this.sectionForm.get('templates');
    control?.setValidators(this.additionalFiles.length ? null : [Validators.required]);
    control?.updateValueAndValidity();
  }

  /**
   * Resolves the CURRENT record's own Documents folder id (and its module),
   * so Browse Files & Folders opens already inside it — e.g. a Proposal
   * opens straight into ITS OWN folder (named after its own number, like
   * "26-41-MM") — instead of landing at the module picker's default/root
   * view and making the user navigate there manually. "(All Modules)" still
   * means the user can navigate elsewhere from there; this only sets where
   * it starts. Same lookup openSaveDocumentPicker() below does inline for
   * the Save flow (left as-is there, not refactored onto this, to avoid
   * touching that already-working method).
   */
  private resolveRecordFolderId(): Observable<number | null> {
    const moduleId = this.data?.folderModuleId ?? this.data?.moduleId;
    const referenceId = this.data?.folderReferenceId ?? this.data?.recordId;
    if (moduleId == null || referenceId == null) {
      return of(null);
    }
    return this.documentService
      .getDocuments({ page: 1, module_id: moduleId, attachable_id: referenceId }, false)
      .pipe(
        map((res: any) => {
          const list = res?.data?.data?.documents?.list ?? [];
          const recordFolder = list.find((item: any) => item?.type === 'folder');
          return recordFolder?.id ?? null;
        }),
        catchError(() => of(null)),
      );
  }

  /**
   * Browse Files & Folders — opens the same reusable in-app file picker
   * (app-document-picker) Proposal's dialog uses, to pick a PDF from ANY
   * module (its own module dropdown covers all of them), but starts
   * already inside the current record's own folder (see
   * resolveRecordFolderId() above) instead of the picker's default view.
   * A picked file is listed in Additional files, same as a dropped
   * file, but — unlike a dropped file — it's a genuine
   * already-stored Document with a real id, merged into the PDF as an
   * additional file.
   */
  openBrowseAllModulesFilePicker(): void {
    if (this.pickerOpen) {
      return;
    }
    this.pickerOpen = true;
    const moduleId = this.data?.folderModuleId ?? this.data?.moduleId;
    const referenceId = this.data?.folderReferenceId ?? this.data?.recordId;

    this.resolveRecordFolderId().subscribe((folderId) => {
      const ref = this.dialog.open(DocumentPickerComponent, {
        width: '860px',
        maxWidth: '95vw',
        panelClass: 'document-picker-dialog',
        autoFocus: false,
        data: {
          selectable: 'file',
          multiple: true,
          accept: this.allowedExtensions,
          allowRecent: true,
          moduleId,
          referenceId,
          folderId,
          // Cosmetic only — see DocumentPickerComponent's folderDisplayName
          // docblock. Reuses the same label each caller already sets for
          // Send Email's subject/filename, not a new field.
          folderDisplayName: this.data?.referenceName,
        },
      });
      ref.afterClosed().subscribe((result: any) => {
        this.pickerOpen = false;
        const files: any[] = result?.files ?? [];
        if (files.length) {
          this.addPickedDocuments(files);
        }
      });
    });
  }

  private addPickedDocuments(files: any[]): void {
    files.forEach((doc) => {
      const id = `doc-${doc?.id}`;
      // Skip a document that's already attached rather than duplicating it.
      if (this.additionalFiles.some((item) => item?.id === id)) {
        return;
      }
      this.additionalFiles.push({
        id,
        name: doc?.original_name || doc?.file_name || 'Untitled',
        isPickedDocument: true,
        document: doc,
      });
    });
    this.syncTemplatesValidator();
  }

  getProposalTemplates() {
    this.isLoadingTemplates = true;

    // Phase 3 additive: if a caller pre-fetched templates (e.g. AiaPayAppDetailDialog uses
    // AiaBillingService instead of the global SharedDataService), use them and skip the fetch.
    if (this.data?.preFetchedTemplates && Array.isArray(this.data.preFetchedTemplates)) {
      this.templates = this.data.preFetchedTemplates;
      const defaultTemplate = this.templates?.find((x: any) => x.is_default == 1);
      if (defaultTemplate) {
        this.sectionForm.get('templates')?.setValue([defaultTemplate]);
      }
      this.isLoadingTemplates = false;
      return;
    }

    let params = this.tenant_id ? {'tenant_id' : this.tenant_id} : {}
    console.log(params, this.tenant_id)
    const service = this.moduleSlug ?  this.sharedDataService.getTemplateByModuleSlug(this.moduleSlug, params) : this.sharedDataService.getTemplateByModule(this.moduleName, params)
    service
      .subscribe({
        next: (response: any) => {
          this.templates = response?.data;
          const defaultTemplate = this.templates?.find(
            (x: any) => x.is_default == 1
          );
          if (defaultTemplate) {
            this.sectionForm.get('templates')?.setValue([defaultTemplate]);
          }
          this.applyCustomerDefaultTemplate();
          this.isLoadingTemplates = false;
        },
        error: (error) => {
          console.error('Error loading templates:', error);
          this.templates = [];
          this.isLoadingTemplates = false;
        }
      });
  }

  /**
   * Every additional file as a stored Document id: a picked document already
   * has one; a local file is first uploaded into this record's own Documents
   * folder (same DocumentService.uploadFile() the Documents panel uses).
   * Emits `null` when a local file couldn't be uploaded.
   */
  private resolveAdditionalDocumentIds(): Observable<any[] | null> {
    // A file already uploaded by an earlier click of this same dialog keeps its id (uploadedDocumentId),
    // so a retry after a failed Preview/Download/Save doesn't upload it again.
    const localFiles = this.additionalFiles.filter((f) => f.isUploadedFile && f.file && f.uploadedDocumentId == null);
    if (localFiles.length === 0) {
      return of(this.additionalFiles.map((f) => f.document?.id ?? f.uploadedDocumentId).filter((id) => id != null));
    }

    const moduleId = this.data?.folderModuleId ?? this.data?.moduleId;
    const referenceId = this.data?.folderReferenceId ?? this.data?.recordId;
    this.processingText = 'Uploading attached files…';
    this.isProcessing = true;
    return this.resolveRecordFolderId().pipe(
      switchMap((folderId) => {
        if (!folderId) {
          this.isProcessing = false;
          this.toasterService.showErrorMessage('Could not upload the attached file(s) for merging. No Documents folder found for this record.');
          return of(null);
        }
        const uploads: Observable<any>[] = localFiles.map((item) =>
          this.documentService.uploadFile(item.file, {
            module_id: moduleId,
            folder_id: folderId,
            attachable_id: referenceId,
            attachment_field: 'file',
            reuse_existing: true, // the server returns this user's identical earlier upload instead of storing a copy
          }).pipe(catchError(() => of(null)))
        );
        return forkJoin(uploads).pipe(
          map((results: any[]) => {
            this.isProcessing = false;
            // Response is double-nested: { data: { success, message, data: { id, ... } } }.
            const uploadedByFile = new Map<any, any>(localFiles.map((item, i) => [item, results[i]?.data?.data?.id]));
            localFiles.forEach((item) => {
              const uploadedId = uploadedByFile.get(item);
              if (uploadedId != null) {
                item.uploadedDocumentId = uploadedId;
              }
            });
            const uploadedIds = [...uploadedByFile.values()].filter((id) => id != null);
            const failedCount = results.length - uploadedIds.length;
            if (failedCount > 0) {
              this.toasterService.showErrorMessage(
                failedCount === results.length
                  ? 'Failed to upload the attached file(s). Merging without them.'
                  : `${failedCount} attached file(s) failed to upload. Merging the rest.`
              );
            }
            // In the list's own order (the order the user arranged), so the merged PDF follows it.
            return this.additionalFiles
              .map((f) => (f.isPickedDocument ? f.document?.id : (uploadedByFile.get(f) ?? f.uploadedDocumentId)))
              .filter((id) => id != null);
          })
        );
      })
    );
  }

  /** `template_id[]=…&document_id[]=…` for the module's generate-pdf / save-document endpoints. */
  private idParams(templateIds: any[], documentIds: any[]): string {
    return [
      ...templateIds.map((id) => `template_id[]=${id}`),
      ...documentIds.map((id) => `document_id[]=${id}`),
    ].join('&');
  }

  /**
   * Download with additional files attached: the dialog does the action
   * itself — one request to the module's own generate-pdf URL, which the
   * backend merges into a single PDF — so none of the many callers need to
   * know about additional files. Closes with an empty `templates` list so
   * their own result handlers do nothing.
   */
  private downloadWithAdditionalFiles(): void {
    if (this.sectionForm.invalid) {
      this.touchFormControls();
      return;
    }
    const generatePdfUrl = this.resolveGeneratePdfUrl();
    if (!generatePdfUrl || !this.data?.recordId) {
      this.toasterService.showErrorMessage('Additional files are not supported for this record.');
      return;
    }
    const templateIds = (this.sectionForm.get('templates')?.getRawValue() ?? []).map((item: any) => item?.id);

    this.resolveAdditionalDocumentIds().subscribe(async (documentIds) => {
      if (documentIds === null) {
        return;
      }
      const userDetail = JSON.parse(localStorage.getItem('auth_detail') || '{}');
      const url = `${generatePdfUrl}?id=${this.data.recordId}&${this.idParams(templateIds, documentIds)}&output=1&user_id=${userDetail?.id}`;

      this.processingText = 'Preparing your PDF…';
      this.isProcessing = true;
      const base = (this.data.referenceName || this.data.recordModuleName || this.data.moduleName || 'document').toString();
      try {
        await downloadPdfFromUrl(await this.pdfLink.sign(url), `${base}.pdf`.replace(/[\\/:*?"<>|]+/g, '-'));
      } catch (error: any) {
        // Keep the dialog open so the files can be fixed or removed.
        this.isProcessing = false;
        this.toasterService.showErrorMessage(error?.message || 'Failed to download the PDF.');
        return;
      }
      this.isProcessing = false;
      this.close({ success: true, templates: [] });
    });
  }

  /**
   * Preview behaves like Save in Documents: spinner while the PDF is prepared
   * (uploading attached files, merging), then the dialog closes and the result
   * opens in the in-app PDF viewer on this same page — no new browser tab.
   */
  private previewInViewer(): void {
    const generatePdfUrl = this.resolveGeneratePdfUrl();
    const selectedTemplates: any[] = this.sectionForm.get('templates')?.getRawValue() ?? [];
    const templateIds = selectedTemplates.map((item: any) => item?.id);

    this.resolveAdditionalDocumentIds().subscribe(async (documentIds) => {
      if (documentIds === null) {
        return;
      }
      const userDetail = JSON.parse(localStorage.getItem('auth_detail') || '{}');
      const url = `${generatePdfUrl}?id=${this.data.recordId}&${this.idParams(templateIds, documentIds)}&output=2&user_id=${userDetail?.id}`;

      this.processingText = 'Preparing preview…';
      this.isProcessing = true;
      try {
        const objectUrl = await fetchPdfAsObjectUrl(await this.pdfLink.sign(url));
        this.isProcessing = false;
        // Same name Save in Documents gives the file (the template's), so the viewer title matches.
        const base = (selectedTemplates[0]?.template_name || this.data.referenceName || this.data.recordModuleName || this.data.moduleName || 'document').toString();
        this.close({ success: true, templates: [] });
        const ref: MatDialogRef<any> = await this.lazyPreview.openPdfPreview({
          width: '90vw',
          height: '90vh',
          maxWidth: '1400px',
          maxHeight: '900px',
          data: {
            file_url: objectUrl,
            original_name: `${base}.pdf`,
            file_type: 'pdf',
            // Edit / Save on this temporary preview: save it to Documents first, then reopen it there.
            saveBeforeEdit: () => this.savePreviewToDocuments(ref, templateIds, documentIds),
          },
          panelClass: 'pdf-preview-dialog-container',
          disableClose: false,
          hasBackdrop: true,
          autoFocus: false,
        });
        ref.afterClosed().subscribe(() => URL.revokeObjectURL(objectUrl));
      } catch (error: any) {
        this.isProcessing = false;
        this.toasterService.showErrorMessage(error?.message && !/^HTTP \d+$/.test(error.message) ? error.message : 'Failed to prepare the preview. Please try again.');
      }
    });
  }

  /**
   * Edit / Save on a Preview: ask for a Documents folder (same picker and default folder as Save In Documents),
   * save the same merged PDF there, close the temporary viewer and open the saved file as a normal Document,
   * where every action works.
   */
  private savePreviewToDocuments(viewerRef: MatDialogRef<any>, templateIds: any[], documentIds: any[]): void {
    if (this.pickerOpen) {
      return;
    }
    const moduleId = this.data.folderModuleId ?? this.data.moduleId;
    const referenceId = this.data.folderReferenceId ?? this.data.recordId;
    if (!this.data.recordId || !this.data.moduleId) {
      this.toasterService.showErrorMessage('This preview cannot be saved to Documents.');
      return;
    }
    this.pickerOpen = true;
    this.toasterService.showInfo('', 'Pick a Documents folder to save this PDF. It then opens ready to edit.');

    const openPicker = (folderId?: any) => {
      const picker = this.dialog.open(DocumentPickerComponent, {
        width: '860px',
        maxWidth: '95vw',
        panelClass: 'document-picker-dialog',
        autoFocus: false,
        data: { selectable: 'folder', multiple: false, moduleId, referenceId, folderId },
      });
      picker.afterClosed().subscribe((result) => {
        this.pickerOpen = false;
        const folder = result?.selectedFolder || result?.folders?.[0];
        if (!folder?.id) {
          return;
        }
        const userDetail = JSON.parse(localStorage.getItem('auth_detail') || '{}');
        const url = `${environment.apiBaseUrl}/save-document?id=${this.data.recordId}&${this.idParams(templateIds, documentIds)}&output=3&user_id=${userDetail?.id}&module_id=${this.data.moduleId}&folder_id=${folder.id}`;
        this.toasterService.showInfo('', 'Saving PDF to Documents…');
        this.documentService.saveDocuments(url).subscribe({
          next: (res: any) => {
            const savedDocument = (res?.data?.documents || []).find((doc: any) => !!doc?.file_url) ?? null;
            this.toasterService.showSuccessMessage('PDF saved to Documents successfully.');
            viewerRef.close();
            this.openSavedDocumentPreview(savedDocument);
          },
          error: () => this.toasterService.showErrorMessage('Failed to save PDF to Documents.'),
        });
      });
    };

    if (moduleId == null || referenceId == null) {
      openPicker();
      return;
    }
    this.documentService
      .getDocuments({ page: 1, module_id: moduleId, attachable_id: referenceId }, false)
      .subscribe({
        next: (res: any) => {
          const list = res?.data?.data?.documents?.list ?? [];
          openPicker(list.find((item: any) => item?.type === 'folder')?.id);
        },
        error: () => openPicker(),
      });
  }

  downloadPdf() {
    if (this.additionalFiles.length > 0) {
      this.downloadWithAdditionalFiles();
      return;
    }
    if (this.sectionForm.invalid) {
      this.touchFormControls();
      return;
    }
    const templateIds = (this.sectionForm.get('templates')?.getRawValue() ?? []).map((item: any) => item?.id);
    this.close({ templates: templateIds, documentIds: [], orderedItems: [], type: 1 });
  }

  previewPdf() {
    if (this.sectionForm.invalid) {
      this.touchFormControls();
      return;
    }
    const formData = (this.sectionForm.get('templates')?.getRawValue() ?? []).map((item: any) => item?.id);
    // In-app viewer ("Preparing preview…", then the same viewer Save in Documents
    // opens) wherever the module has a generate-pdf route and a record; several
    // templates and/or additional files are merged into one PDF by the backend.
    // Modules without a route (e.g. accounting reports) keep their caller's preview.
    if (this.canAttachFiles) {
      this.previewInViewer();
      return;
    }
    this.close({ templates: formData, type: 2 });
  }

  /**
   * Module → its existing PDF preview endpoint path. Lets Send Email work for EVERY caller
   * that passes a recognised `moduleName`/`moduleSlug` (+ a `recordId`) without each caller
   * having to pass `generatePdfUrl` explicitly. Only modules with a real generate-pdf route
   * are listed; anything else (scope, vendor-quotes, accounting slugs, …) stays unavailable.
   */
  private static readonly GENERATE_PDF_PATHS: { [key: string]: string } = {
    'ticket': '/ticket/generate-pdf',
    'bid': '/bids/generate-pdf',
    'bids': '/bids/generate-pdf',
    'proposal': '/generate-pdf',
    'submittal': '/submittal/generate-pdf',
    'change order': '/change-order/generate-pdf',
    'change-order': '/change-order/generate-pdf',
    'subcontractor': '/subcontractor/generate-pdf',
    'warehouse': '/warehouse/generate-pdf',
    'service': '/service/generate-pdf',
    'product': '/product/generate-pdf',
    'vendor': '/vendor/generate-pdf',
    'customer': '/customer/generate-pdf',
    'staff': '/hr-staff/generate-pdf',
    'job': '/jobs/generate-pdf',
    'note': '/note/generate-pdf',
    'task': '/task/generate-pdf',
    'jobsite': '/jobsite/generate-pdf',
  };

  /**
   * Resolve the PDF endpoint for Send Email: an explicit `data.generatePdfUrl` wins; otherwise
   * derive it from the caller's moduleName/moduleSlug. Returns null when the module has no
   * known generate-pdf route (Send Email then reports it's unavailable).
   */
  private resolveGeneratePdfUrl(): string | null {
    if (this.data?.generatePdfUrl) { return this.data.generatePdfUrl; }
    const key = (this.data?.moduleName || this.data?.moduleSlug || '').toString().trim().toLowerCase();
    const path = ExportPdfSharedComponent.GENERATE_PDF_PATHS[key];
    return path ? (environment.apiBaseUrl + path) : null;
  }

  /**
   * Generate the selected template(s) as PDF(s) via the module's EXISTING preview endpoint
   * (explicit `data.generatePdfUrl`, else derived from moduleName/moduleSlug), then open the
   * email compose modal linked to this record with those PDFs pre-attached. No backend change
   * and no new route — reuses each module's own preview URL.
   */
  async sendEmail(): Promise<void> {
    if (this.sectionForm.invalid) {
      this.touchFormControls();
      return;
    }
    const generatePdfUrl = this.resolveGeneratePdfUrl();
    if (!generatePdfUrl || !this.data?.recordId) {
      this.toasterService.showError('Emailing is not available for this record.');
      return;
    }

    const selected = this.sectionForm.get('templates')?.getRawValue() || [];
    const userDetail = JSON.parse(localStorage.getItem('auth_detail') || '{}');
    const base = (this.data.referenceName || this.data.recordModuleName || this.data.moduleName || 'document')
      .toString();

    this.buttonStates.email = 'loading';
    this.isProcessing = true;

    try {
      // One PDF per selected template, fetched from the same preview endpoint
      // (output=2 = inline). CORS is open + the route authenticates via user_id.
      const files: File[] = await Promise.all(
        selected.map(async (t: any) => {
          const url = `${generatePdfUrl}?id=${this.data.recordId}`
            + `&template_id=${t.id}&output=2&user_id=${userDetail?.id}`;
          const resp = await fetch(url, { mode: 'cors', cache: 'reload' });
          if (!resp.ok) {
            throw new Error(`HTTP ${resp.status}`);
          }
          const blob = await resp.blob();
          const name = `${base} - ${t.template_name || t.id}.pdf`.replace(/[\\/:*?"<>|]+/g, '-');
          return new File([blob], name, { type: blob.type || 'application/pdf' });
        })
      );

      this.dialog.open(ComposeEmailComponent, {
        width: '700px',
        height: '600px',
        panelClass: 'compose-email-dialog-container',
        data: {
          mode: 'compose',
          moduleId: this.data.moduleId,
          moduleDataId: this.data.recordId,
          moduleName: this.data.recordModuleName || this.data.moduleName,
          referenceName: this.data.referenceName,
          lockModuleLink: true,
          // Prefill subject like the module email launcher does. Caller may pass
          // an explicit `emailSubject`; otherwise fall back to the record ref.
          prefillSubject: this.data.emailSubject || this.data.referenceName,
          preAttachedFiles: files,
        },
      });

      this.buttonStates.email = 'normal';
      this.isProcessing = false;
      this.close({ emailed: true });
    } catch (e) {
      this.buttonStates.email = 'error';
      this.isProcessing = false;
      this.toasterService.showError('Could not generate the PDF to email. Please try again.');
    }
  }

  close = (data: any = null) => {
    this.dialogRef.close(data);
  };

  touchFormControls() {
    Object.keys(this.sectionForm.controls).forEach((key) => {
      this.sectionForm.get(key)?.markAsTouched();
    });
  }

  // /**
  //  * Writes a small branded "generating" page into a pre-opened tab so it
  //  * doesn't sit blank while the save is in progress — swapped for the real
  //  * PDF via `location.href` once the save completes.
  //  */
  // private writePdfLoadingPlaceholder(win: Window | null): void {
  //   if (!win) {
  //     return;
  //   }
  //   win.document.write(`
  //     <!DOCTYPE html>
  //     <html>
  //       <head>
  //         <title>Generating PDF...</title>
  //         <style>
  //           body {
  //             margin: 0;
  //             height: 100vh;
  //             display: flex;
  //             flex-direction: column;
  //             align-items: center;
  //             justify-content: center;
  //             gap: 16px;
  //             font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  //             background-color: #FAFAFA;
  //             color: #28304E;
  //           }
  //           .spinner {
  //             width: 40px;
  //             height: 40px;
  //             border: 4px solid #E5E5E5;
  //             border-top-color: #FF6600;
  //             border-radius: 50%;
  //             animation: spin 1s linear infinite;
  //           }
  //           @keyframes spin {
  //             to { transform: rotate(360deg); }
  //           }
  //         </style>
  //       </head>
  //       <body>
  //         <div class="spinner"></div>
  //         <div>Generating your PDF…</div>
  //       </body>
  //     </html>
  //   `);
  //   win.document.close();
  // }

  saveDocuments() {
     if(this.sectionForm.invalid) {
      this.touchFormControls();
      return;
    }
    this.buttonStates.save = 'loading';
    this.isProcessing = true;
    const formData = (this.sectionForm.get('templates')?.getRawValue() ?? []).map((item: any) => item?.id);
    this.close({templates:formData,type:3});
  }

  /**
   * "Save Document": open the in-app document picker (Select File popup),
   * pre-opened directly inside the current record's own folder — same
   * pattern the header camera/screenshot feature uses (resolve the record's
   * folder id first, then hand it to the picker as `folderId` so it opens
   * already inside it, not just showing it as a tile to click into).
   */
  openSaveDocumentPicker(): void {
    if (this.pickerOpen) {
      return;
    }
    this.pickerOpen = true;
    // Save destination defaults to this record's own folder, but a caller
    // may override it to point at a related record's folder instead (e.g.
    // Proposal -> its Bid, Submittal/Change Order -> their Job) via
    // folderModuleId/folderReferenceId — the generated PDF's content is
    // unaffected, since that's driven separately by data.recordId/moduleId
    // in the save URL below, not by these.
    const moduleId = this.data.folderModuleId ?? this.data.moduleId;
    const referenceId = this.data.folderReferenceId ?? this.data.recordId;

    if (moduleId == null || referenceId == null) {
      this.openDocumentPicker(moduleId, referenceId);
      return;
    }

    this.documentService
      .getDocuments({ page: 1, module_id: moduleId, attachable_id: referenceId }, false)
      .subscribe({
        next: (res: any) => {
          const list = res?.data?.data?.documents?.list ?? [];
          const recordFolder = list.find((item: any) => item?.type === 'folder');
          this.openDocumentPicker(moduleId, referenceId, recordFolder?.id);
        },
        error: () => this.openDocumentPicker(moduleId, referenceId),
      });
  }

  private openDocumentPicker(moduleId: any, referenceId: any, folderId?: any): void {
    const ref = this.dialog.open(DocumentPickerComponent, {
      width: '860px',
      maxWidth: '95vw',
      panelClass: 'document-picker-dialog',
      autoFocus: false,
      data: {
        selectable: 'folder',
        multiple: false,
        moduleId,
        referenceId,
        folderId,
      },
    });
    ref.afterClosed().subscribe((result) => {
      this.pickerOpen = false;
      const folder = result?.selectedFolder || result?.folders?.[0];
      if (!folder?.id || !this.data.recordId || !this.data.moduleId) {
        if (result) {
          console.log('[ExportPdfShared] document picker result:', result);
        }
        return;
      }

      if (this.sectionForm.invalid) {
        this.touchFormControls();
        return;
      }

      const userDetail = JSON.parse(localStorage.getItem('auth_detail') || '{}');
      const templateIds: any[] = (this.sectionForm.get('templates')?.getRawValue() || []).map((t: any) => t.id);

      this.buttonStates.save = 'loading';
      this.isProcessing = true;

      // Additional files are merged with the template(s) into ONE saved PDF
      // (single request — the backend's generic merge); with none attached,
      // the original behavior stays: one saved file per selected template.
      this.resolveAdditionalDocumentIds().subscribe((documentIds) => {
        if (documentIds === null) {
          this.buttonStates.save = 'error';
          this.isProcessing = false;
          return;
        }
        this.processingText = 'Saving PDF to Documents…';
        this.isProcessing = true;

        const saveUrls: string[] =
          documentIds.length > 0
            ? [`${environment.apiBaseUrl}/save-document?id=${this.data.recordId}&${this.idParams(templateIds, documentIds)}&output=3&user_id=${userDetail?.id}&module_id=${this.data.moduleId}&folder_id=${folder.id}`]
            : templateIds.map((templateId: any) => `${environment.apiBaseUrl}/save-document?id=${this.data.recordId}&template_id=${templateId}&output=3&user_id=${userDetail?.id}&module_id=${this.data.moduleId}&folder_id=${folder.id}`);

        let remaining = saveUrls.length;
        let hadError = false;
        // First file that was saved — opened in the Documents preview once the save has finished.
        let firstSavedDocument: any = null;

        saveUrls.forEach((url) => {
          this.documentService.saveDocuments(url).subscribe({
            next: (res: any) => {
              firstSavedDocument =
                firstSavedDocument ?? (res?.data?.documents || []).find((doc: any) => !!doc?.file_url) ?? null;

              remaining--;
              if (remaining === 0) {
                this.buttonStates.save = 'normal';
                this.isProcessing = false;
                if (!hadError) {
                  this.toasterService.showSuccessMessage('PDF saved to Documents successfully.');
                  this.close({ success: true, templates: [] });
                  this.openSavedDocumentPreview(firstSavedDocument);
                }
              }
            },
            error: () => {
              hadError = true;
              remaining--;
              this.buttonStates.save = 'error';
              this.isProcessing = false;
              this.toasterService.showErrorMessage('Failed to save PDF to Documents.');
            },
          });
        });
      });
    });
  }

  /**
   * Opens the file that was just saved in the same PDF preview the Documents tab opens from
   * its eye icon (see ModuleDocumentsComponent's PDF branch), so the user sees the saved PDF
   * right away. Uses the root-provided LazyPreviewService, so it still opens after this
   * dialog has closed itself.
   */
  private openSavedDocumentPreview(doc: any): void {
    if (!doc?.file_url) {
      return;
    }
    this.lazyPreview.openPdfPreview({
      width: '90vw',
      height: '90vh',
      maxWidth: '1400px',
      maxHeight: '900px',
      data: {
        id: doc.id,
        file_url: doc.file_url,
        original_name: doc.original_name,
        file_type: 'pdf',
      },
      panelClass: 'pdf-preview-dialog-container',
      disableClose: false,
      hasBackdrop: true,
      autoFocus: false,
    });
  }

  // Helper methods for enhanced functionality
  getSelectedTemplatesCount(): number {
    const selectedTemplates = this.sectionForm.get('templates')?.value;
    return selectedTemplates ? selectedTemplates.length : 0;
  }

  clearAllTemplates(): void {
    this.sectionForm.get('templates')?.setValue([]);
    this.sectionForm.get('templates')?.markAsTouched();
  }

  trackByTemplateId(index: number, template: any): any {
    return template?.id || index;
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
